/**
 * Dynamic Messaging Scroll-driven Carousel
 * Handles scroll-based image crossfade and text sliding
 */
import Component from '../vendor/Component'

const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)')
const MOBILE_QUERY = window.matchMedia('(max-width: 767px)')

function getOffsetTop (el) {
  const rect = el.getBoundingClientRect()
  return rect.top + (window.scrollY || window.pageYOffset)
}

export default class DynamicMessaging extends Component {
  constructor (elem, theme, options, ctx) {
    super(elem, theme, options, ctx)

    this._container = null
    this._images = []
    this._slideTexts = []
    this._wordElements = []
    this._textsContainer = null
    this._slideCount = 0

    this._scrollTicking = false
    this._boundOnScroll = this._onScroll.bind(this)
    this._boundOnResize = this._onResize.bind(this)
  }

  mount () {
    const section = this._elem

    this._container = section.querySelector('[data-dynamic-messaging-scroll-el="container"]')
    this._images = Array.from(section.querySelectorAll('[data-dynamic-messaging-scroll-el="image-content"]'))
    this._textsContainer = section.querySelector('[data-dynamic-messaging-scroll-el="texts"]')
    this._slideTexts = Array.from(section.querySelectorAll('[data-dynamic-messaging-scroll-el="slide-content"]'))

    const textRevealOriginal = section.querySelector('[data-text-reveal-original]')
    const textRevealOverlay = section.querySelector('[data-text-reveal-overlay]')

    if (!this._images.length || !this._slideTexts.length || !this._container || !this._textsContainer) return

    this._slideCount = Math.max(this._images.length, this._slideTexts.length)

    if (textRevealOriginal && textRevealOverlay) {
      const text = textRevealOriginal.innerText.trim()
      const wordsArray = text.split(' ')
      let buildContent = ''

      wordsArray.forEach((word, index) => {
        buildContent += `<span class="sec-DynamicMessaging_Word">${word}</span>`
        if (index < wordsArray.length - 1) {
          buildContent += ' '
        }
      })

      textRevealOverlay.innerHTML = buildContent
      this._wordElements = Array.from(textRevealOverlay.querySelectorAll('.sec-DynamicMessaging_Word'))
    }

    this._updateSectionHeight()

    window.addEventListener('scroll', this._boundOnScroll, { passive: true })
    window.addEventListener('resize', this._boundOnResize, { passive: true })

    // Initial state after layout is ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this._applyScrollState())
    })
  }

  unmount () {
    window.removeEventListener('scroll', this._boundOnScroll)
    window.removeEventListener('resize', this._boundOnResize)

    // Reset any inline styles
    this._images.forEach((img) => { img.style.opacity = '' })
    this._slideTexts.forEach((text) => {
      text.style.transform = ''
      text.classList.remove('is-active')
    })
    this._wordElements.forEach((wordEl) => {
      wordEl.style.removeProperty('--Reveal_Amount')
    })
    this._elem.style.minHeight = ''
  }

  _onScroll () {
    if (!this._scrollTicking) {
      this._scrollTicking = true
      requestAnimationFrame(() => {
        this._scrollTicking = false
        this._applyScrollState()
      })
    }
  }

  _onResize () {
    this._updateSectionHeight()
    // Debounce the scroll state recalculation slightly after resize
    if (!this._scrollTicking) {
      this._scrollTicking = true
      requestAnimationFrame(() => {
        this._scrollTicking = false
        this._applyScrollState()
      })
    }
  }

  _updateSectionHeight () {
    const isMobile = MOBILE_QUERY.matches
    const viewportHeight = window.innerHeight
    const titleRevealHeight = isMobile ? viewportHeight * 0.3 : viewportHeight * 0.8

    if (isMobile) {
      this._elem.style.minHeight = `${titleRevealHeight + viewportHeight * 0.8}px`
    } else {
      const scrollPerSlide = viewportHeight * 0.6
      const slidesHeight = scrollPerSlide * (this._slideCount - 1)
      const totalScrollHeight = titleRevealHeight + slidesHeight + viewportHeight * 1.2
      this._elem.style.minHeight = `${totalScrollHeight}px`
    }
  }

  _applyScrollState () {
    const scrollY = window.scrollY || window.pageYOffset
    const sectionTop = getOffsetTop(this._elem)
    const viewportHeight = window.innerHeight
    const isMobile = MOBILE_QUERY.matches
    const titleRevealHeight = isMobile ? viewportHeight * 0.3 : viewportHeight * 0.8
    const titleRevealStart = isMobile ? sectionTop - (viewportHeight / 2) : sectionTop
    const titleRevealEnd = titleRevealStart + titleRevealHeight

    if (scrollY < titleRevealStart) {
      this._revealTitle(0)
      if (!isMobile) this._setActiveSlide(0)
      return
    }

    if (scrollY <= titleRevealEnd) {
      const titleProgress = (scrollY - titleRevealStart) / titleRevealHeight
      this._revealTitle(titleProgress)
      if (!isMobile) this._setActiveSlide(0)
      return
    }

    this._revealTitle(1)

    if (isMobile) return

    const slidesStart = titleRevealEnd
    const scrollPerSlide = viewportHeight * 0.6
    const slidesHeight = scrollPerSlide * (this._slideCount - 1)
    const slidesEnd = slidesStart + slidesHeight

    if (scrollY > slidesEnd) {
      this._setActiveSlide(this._slideCount - 1)
      return
    }

    const slideProgress = (scrollY - slidesStart) / slidesHeight
    const slideIndex = Math.round(slideProgress * (this._slideCount - 1))
    this._setActiveSlide(slideIndex)
  }

  _revealTitle (progress) {
    if (!this._wordElements.length) return
    if (REDUCED_MOTION_QUERY.matches) {
      this._wordElements.forEach((wordEl) => wordEl.style.setProperty('--Reveal_Amount', '100%'))
      return
    }

    const clampedProgress = Math.max(0, Math.min(1, progress))

    if (clampedProgress === 0) {
      this._wordElements.forEach((wordEl) => wordEl.style.setProperty('--Reveal_Amount', '0%'))
      return
    }

    if (clampedProgress === 1) {
      this._wordElements.forEach((wordEl) => wordEl.style.setProperty('--Reveal_Amount', '100%'))
      return
    }

    const wordCount = this._wordElements.length
    this._wordElements.forEach((wordEl, index) => {
      const minimumPercent = index / wordCount
      const maximumPercent = (index + 1) / wordCount

      if (clampedProgress >= maximumPercent) {
        wordEl.style.setProperty('--Reveal_Amount', '100%')
      } else if (clampedProgress >= minimumPercent && clampedProgress < maximumPercent) {
        const wordProgress = (clampedProgress - minimumPercent) / (maximumPercent - minimumPercent)
        const wordProgressAmount = Math.round(wordProgress * 100 * 10) / 10
        wordEl.style.setProperty('--Reveal_Amount', `${wordProgressAmount}%`)
      } else {
        wordEl.style.setProperty('--Reveal_Amount', '0%')
      }
    })
  }

  _setActiveSlide (index) {
    const safeIndex = Math.max(0, Math.min(index, this._slideCount - 1))

    this._images.forEach((img, i) => {
      img.style.opacity = i === safeIndex ? 1 : 0
    })

    const containerHeight = this._textsContainer.offsetHeight
    const rowHeight = containerHeight / 3
    const totalTranslate = -safeIndex * rowHeight

    this._slideTexts.forEach((text, i) => {
      text.style.transform = `translateY(${totalTranslate}px)`
      text.classList.toggle('is-active', i === safeIndex)
    })
  }
}
